import { isObject } from "../core/normalize.js";
import type { Item, RawSearchResult } from "../core/types.js";

/** Client copy of the server's pages pseudo-collection id (src/lib/constants.ts). */
export const PAGES_COLLECTION_ID = "__webflow_pages__";

function strOrNull(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

/**
 * Flattens a `/search` result into the shape the list endpoint serves, so every
 * `data-slater-bind-*` works identically on a search card: raw top-level keys
 * first, then `fieldData` spread over them (mirroring the server's shapeItem),
 * then the reserved computed keys on top so they always win.
 *
 * `collection` is the template-picker key — the collection slug, or the literal
 * `"page"` for a static page.
 *
 * `url` prefers the server-resolved `path`, which is exact: a page's published path,
 * or a CMS item's path built from its collection's real template-page prefix. It falls
 * back to the old best-effort guess — `/{collection_slug}/{slug}`, Webflow's DEFAULT
 * detail-page shape — for responses from a backend that predates `path`, or for a
 * collection whose template page hasn't been seen by a sync yet. That fallback is
 * wrong for any collection with a customized detail path; a template can override it
 * with a `data-slater-href` pattern of its own.
 */
export function normalizeSearchResult(raw: RawSearchResult): Item {
  const { fieldData, ...rest } = raw;
  const isPage = raw.collection_id === PAGES_COLLECTION_ID;
  const slug = strOrNull(raw.slug);
  const collectionSlug = strOrNull(raw.collection_slug) ?? "";
  const url =
    strOrNull(raw.path) ??
    (isPage
      ? strOrNull(raw.publishedPath) ?? ""
      : collectionSlug && slug
        ? `/${collectionSlug}/${slug}`
        : "");

  return {
    ...rest,
    createdOn: strOrNull(raw.createdOn),
    lastUpdated: strOrNull(raw.lastUpdated),
    lastPublished: strOrNull(raw.lastPublished),
    ...(isObject(fieldData) ? fieldData : {}),
    id: raw.id,
    name: strOrNull(raw.name),
    slug,
    matchedContext: typeof raw.matchedContext === "string" ? raw.matchedContext : "",
    collection: isPage ? "page" : collectionSlug,
    url,
  };
}
